import { useCallback, useContext } from 'react';

import { TokenContext } from '@/Contexts/TokenContext';
import { DeleteReviewerRequestProps } from '@/models/DeleteReviewerRequest';
import { QuestionReviewProps } from '@/models/QuestionReview';
import { ReviewerAssignmentProps } from '@/models/ReviewerAssignment';
import { ReviewerInvitationProps } from '@/models/ReviewerInvitation';
import { baseUrl } from '@/utils/constants';

import { useCustomFetch } from './useCustomFetch';

export default function useReviewInformation() {
  const { authResult } = useContext(TokenContext);
  const { customFetch } = useCustomFetch();

  const getReviewerAssignments = useCallback(
    async (stateId?: string): Promise<ReviewerAssignmentProps[]> => {
      const fetchUrl = `${baseUrl}reviewerassignments${stateId ? `?stateId=${stateId}` : ''}`;

      const response = await customFetch<ReviewerAssignmentProps[]>(fetchUrl, {
        method: 'GET',
        headers: {
          accept: 'application/json',
          'content-type': 'application/json',
          Authorization: `Bearer ${authResult.accessToken}`,
        },
      });

      return response;
    },
    [authResult, customFetch]
  );

  const postReviewerInvitations = useCallback(
    async (info: any) => {
      const fetchUrl = `${baseUrl}reviewerinvitations`;

      const response = await customFetch(fetchUrl, {
        method: 'POST',
        headers: {
          accept: 'application/json',
          'content-type': 'application/json',
          Authorization: `Bearer ${authResult.accessToken}`,
        },
        body: JSON.stringify(info),
      });

      return response;
    },
    [authResult, customFetch]
  );

  const getReviewerInvitations = useCallback(async (): Promise<ReviewerInvitationProps[]> => {
    const fetchUrl = `${baseUrl}reviewerinvitations`;

    const response = await customFetch<ReviewerInvitationProps[]>(fetchUrl, {
      method: 'GET',
      headers: {
        accept: 'application/json',
        'content-type': 'application/json',
        Authorization: `Bearer ${authResult.accessToken}`,
      },
    });

    return response;
  }, [authResult, customFetch]);

  const postReviewerAssignments = useCallback(
    async (info: any) => {
      const fetchUrl = `${baseUrl}reviewerassignments`;

      const response = await customFetch(fetchUrl, {
        method: 'POST',
        headers: {
          accept: 'application/json',
          'content-type': 'application/json',
          Authorization: `Bearer ${authResult.accessToken}`,
        },
        body: JSON.stringify(info),
      });

      return response;
    },
    [authResult, customFetch]
  );

  const deleteReviewerAssignments = useCallback(
    async (info: DeleteReviewerRequestProps) => {
      const fetchUrl = `${baseUrl}reviewerassignments`;

      const response = await customFetch(fetchUrl, {
        method: 'DELETE',
        headers: {
          accept: 'application/json',
          'content-type': 'application/json',
          Authorization: `Bearer ${authResult.accessToken}`,
        },
        body: JSON.stringify(info),
      });

      return response;
    },
    [authResult, customFetch]
  );

  const postReviewerComment = useCallback(
    async (info: any): Promise<QuestionReviewProps | string | null | { errors: string }> => {
      const fetchUrl = `${baseUrl}questionreviews`;

      const response = await customFetch<QuestionReviewProps | string | null | { errors: string }>(
        fetchUrl,
        {
          method: 'POST',
          headers: {
            accept: 'application/json',
            'content-type': 'application/json',
            Authorization: `Bearer ${authResult.accessToken}`,
          },
          body: JSON.stringify(info),
        }
      );

      return response;
    },
    [authResult, customFetch]
  );

  const postQuestionReviewResolved = useCallback(
    async (
      applicationId: number | string,
      questionId: number | string,
      resolved: boolean
    ): Promise<QuestionReviewProps[]> => {
      const fetchUrl = `${baseUrl}questionreviews/updatequestionreviewsresolved`;

      const request = {
        applicationId: applicationId,
        questionId: questionId,
        resolved: resolved,
      };

      const response = await customFetch<QuestionReviewProps[]>(fetchUrl, {
        method: 'PUT',
        headers: {
          accept: 'application/json',
          'content-type': 'application/json',
          Authorization: `Bearer ${authResult.accessToken}`,
        },
        body: JSON.stringify(request),
      });

      return response;
    },
    [authResult, customFetch]
  );

  return {
    getReviewerAssignments,
    postReviewerAssignments,
    deleteReviewerAssignments,
    getReviewerInvitations,
    postReviewerInvitations,
    postReviewerComment,
    postQuestionReviewResolved,
  };
}
